import type {
  Input as RtgInput,
  PasswordInput as RtgPasswordInput,
} from "@revotech-group/revotech-ui-kit";
import { html, LitElement, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import { NAMESPACE } from "@/constants";
import { genRandSeed } from "@/utils";

import type { SbPasswordField } from "./_password-field";
import {
  CONFIRM_FIELD_DEFAULT_PROPS as DEFAULT_PROPS,
  genRequiredError,
} from "./field.defs";
import type {
  ConfirmFieldAutocomplete,
  ConfirmFieldPart,
  ConfirmFieldValidateType,
  ConfirmFieldValidation,
  ConfirmFieldVariant,
  SbConfirmFieldProps as Props,
} from "./field.types";

/**
 * Self-validating confirm password input field for use in auth widget forms.
 *
 * @part confirm-field - rtg-field: root
 * @part confirm-field-label - rtg-field-label
 * @part confirm-field-mark - span: decorative marker text/caret in label
 * @part confirm-field-description - rtg-field-description
 * @part confirm-field-input - rtg-input/rtg-password-input
 * @part confirm-field-error - rtg-field-error: inline validation error message
 *
 * @event sb-confirm-field:validate - Fired when validation runs, on form submit or via the public `validate()` method.
 *  Detail: `{ id: string; inputId: string; name: string; value: string; valid: boolean; type: ConfirmFieldValidateType; pair: { selector: string; value: string }; message: string }`
 *    - type: `"success" | "required" | "match"`
 */
@customElement("sb-confirm-field")
export class SbConfirmField extends LitElement implements Props {
  static readonly ROOT = "confirm-field";
  static readonly LABEL = "label";
  static readonly MARK = "mark";
  static readonly DESCRIPTION = "description";
  static readonly INPUT = "input";
  static readonly ERROR = "error";

  static readonly TAG = `${NAMESPACE}-${SbConfirmField.ROOT}`;

  static readonly VALIDATE_EVENT = `${SbConfirmField.TAG}:validate`;

  static readonly PARTS: Record<ConfirmFieldPart, string> = {
    root: SbConfirmField.ROOT,
    label: `${SbConfirmField.ROOT}-${SbConfirmField.LABEL}`,
    mark: `${SbConfirmField.ROOT}-${SbConfirmField.MARK}`,
    description: `${SbConfirmField.ROOT}-${SbConfirmField.DESCRIPTION}`,
    input: `${SbConfirmField.ROOT}-${SbConfirmField.INPUT}`,
    error: `${SbConfirmField.ROOT}-${SbConfirmField.ERROR}`,
  };

  static readonly INPUT_SELECTORS = `rtg-input[part="${SbConfirmField.PARTS.input}"], rtg-password-input[part="${SbConfirmField.PARTS.input}"]`;

  /* ------------------------------------------------------------ */

  /**
   * Substring used in generated part IDs.
   */
  @property({ type: String })
  seed = genRandSeed();

  /**
   * Custom ID used as the root part ID and as the base of subpart IDs.
   */
  @property({ type: String, attribute: "custom-id" })
  customId?: string;

  /**
   * ID given to the input part, forwarded to its inner input element.
   */
  private _inputId?: string;

  private get _defaultInputId() {
    return `${this.rootId}-${SbConfirmField.INPUT}`;
  }

  @property({ type: String, attribute: "input-id" })
  get inputId(): string {
    return this._inputId ?? this._defaultInputId;
  }

  set inputId(value: string | null | undefined) {
    const old = this.inputId;
    this._inputId = value || undefined;
    this.requestUpdate("inputId", old);
  }

  /**
   * HTML `name` attribute forwarded to the inner input, used as the form data
   * key on submission.
   */
  private _name = DEFAULT_PROPS.name;

  @property({ type: String })
  get name() {
    return this._name;
  }

  set name(value: string) {
    const old = this._name;
    this._name = value.length ? value : DEFAULT_PROPS.name;
    this.requestUpdate("name", old);
  }

  /**
   * Pre-fills the input until the user types and their input takes over.
   * Resetting the form restores this value.
   */
  @property({ type: String, attribute: "default-value" })
  defaultValue?: string;

  /**
   * Text used in the label part of the field.
   */
  private _label = DEFAULT_PROPS.label;

  @property({ type: String })
  get label() {
    return this._label;
  }

  set label(value: string) {
    const old = this._label;
    this._label = value.length ? value : DEFAULT_PROPS.label;
    this.requestUpdate("label", old);
  }

  /**
   * Placeholder text shown inside the input when empty.
   */
  @property({ type: String })
  placeholder?: string;

  /**
   * Text used in the description part of the field.
   */
  @property({ type: String })
  description?: string;

  /**
   * Controls the variation of input element rendered.
   * When `"toggle"`, a toggleable input with a hide/show button is rendered.
   */
  @property({ type: String })
  variant: ConfirmFieldVariant = DEFAULT_PROPS.variant;

  /**
   * Custom text used in the mark part of the field label.
   */
  @property({ type: String })
  mark?: string;

  /**
   * When provided with `required`, the mark part is rendered with an asterisk.
   * Has no effect if a custom `mark` text is provided.
   */
  @property({ type: Boolean })
  caret?: boolean;

  /**
   * HTML `autocomplete` hint forwarded to the inner input element.
   */
  @property({ type: String })
  autocomplete?: ConfirmFieldAutocomplete;

  /**
   * Disables the field and its inner input element, preventing interaction.
   */
  @property({ type: Boolean })
  disabled?: boolean;

  /**
   * Marks the field as required: validation fails if field is empty.
   */
  @property({ type: Boolean })
  required?: boolean;

  /**
   * CSS selector for the paired password field element.
   */
  @property({ type: String })
  pair = DEFAULT_PROPS.pair;

  /**
   * Controls validation depth, only enforcing the required check if `"simple"`.
   */
  @property({ type: String })
  validation: ConfirmFieldValidation = DEFAULT_PROPS.validation;

  /**
   * Error message used when the field is required but empty.
   */
  private _requiredError?: string;

  private get _defaultRequiredError() {
    return genRequiredError(this.label);
  }

  @property({ type: String, attribute: "required-error" })
  get requiredError(): string {
    return this._requiredError ?? this._defaultRequiredError;
  }

  set requiredError(value: string | null | undefined) {
    const old = this.requiredError;
    this._requiredError = value || undefined;
    this.requestUpdate("requiredError", old);
  }

  /**
   * Error message used when the field and pair field values do not match.
   */
  private _matchError = DEFAULT_PROPS.matchError;

  @property({ type: String, attribute: "match-error" })
  get matchError() {
    return this._matchError;
  }

  set matchError(value: string) {
    const old = this._matchError;
    this._matchError = value.length ? value : DEFAULT_PROPS.matchError;
    this.requestUpdate("matchError", old);
  }

  /* ---------------------------------------- */

  /**
   * Tracks whether the field is currently in an invalid state.
   */
  @state()
  private _invalid = false;

  /**
   * Error message displayed in the error part when the field is invalid.
   */
  @state()
  private _error = "";

  /**
   * Raw value of the confirm field input, synced on each input event.
   */
  @state()
  private _value = "";

  /* ---------------------------------------- */

  /**
   * Gates whether `defaultValue` or `_value` is authoritative for `this.value`.
   * True once the user has typed into the input.
   */
  private _dirty = false;

  /**
   * Reference to the nearest `<form>` ancestor.
   */
  private _form: HTMLFormElement | null = null;

  /* ------------------------------------------------------------ */

  protected createRenderRoot() {
    return this;
  }

  /* ------------------------------------------------------------ */

  get rootId() {
    return this.customId ?? `${SbConfirmField.TAG}-${this.seed}`;
  }

  get labelId() {
    return `${this.rootId}-${SbConfirmField.LABEL}`;
  }

  get markId() {
    return `${this.rootId}-${SbConfirmField.MARK}`;
  }

  get descriptionId() {
    return `${this.rootId}-${SbConfirmField.DESCRIPTION}`;
  }

  get errorId() {
    return `${this.rootId}-${SbConfirmField.ERROR}`;
  }

  /**
   * Normalized value of the password field input.
   */
  get value() {
    const defaultValue = this.defaultValue ?? "";
    const value = this._dirty ? this._value : defaultValue;

    return value;
  }

  /**
   * Value of paired password field, returning an empty string if not found.
   */
  get pairValue() {
    const scope = this._form ?? document;
    const field = scope.querySelector(this.pair) as SbPasswordField | null;
    return field ? field.value : "";
  }

  /* ------------------------------------------------------------ */

  updated(changed: PropertyValues) {
    if (changed.has("defaultValue") && !this._dirty) {
      const selectors = SbConfirmField.INPUT_SELECTORS;
      const input = this.querySelector(selectors) as
        | RtgPasswordInput
        | RtgInput
        | null;

      if (input) input.value = this.defaultValue ?? "";
    }
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("input", this._handleInput);

    this._form = this.closest("form");
    if (this._form === null) return;

    this._form.noValidate = true;
    this._form.addEventListener("submit", this._handleFormSubmit);
    this._form.addEventListener("reset", this._handleFormReset);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener("input", this._handleInput);

    if (this._form === null) return;

    this._form.removeEventListener("submit", this._handleFormSubmit);
    this._form.removeEventListener("reset", this._handleFormReset);
    this._form = null;
  }

  /* ------------------------------------------------------------ */

  /**
   * Triggers validation and returns `true` if the current value is valid,
   * `false` otherwise.
   */
  validate() {
    return this._validate();
  }

  /* ---------------------------------------- */

  private _dispatchValidate(
    valid: boolean,
    type: ConfirmFieldValidateType,
    message: string,
  ) {
    const { rootId: id, inputId, name, value } = this;
    const pair = { selector: this.pair, value: this.pairValue };

    this.dispatchEvent(
      new CustomEvent(SbConfirmField.VALIDATE_EVENT, {
        detail: { id, inputId, name, value, valid, type, pair, message },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _onValidateSuccess() {
    this._invalid = false;
    this._error = "";
    this._dispatchValidate(true, "success", "Valid confirm.");
  }

  private _onValidateError(type: ConfirmFieldValidateType, message: string) {
    this._invalid = true;
    this._error = message;
    this._dispatchValidate(false, type, message);
  }

  private _validate() {
    const value = this.value;

    if (this.required && !value) {
      this._onValidateError("required", this.requiredError);
      return false;
    }

    if (!value || this.validation === "simple") {
      this._onValidateSuccess();
      return true;
    }

    if (value !== this.pairValue) {
      this._onValidateError("match", this.matchError);
      return false;
    }

    this._onValidateSuccess();
    return true;
  }

  /* ------------------------------------------------------------ */

  private _handleInput = (e: Event) => {
    const target = e.target as HTMLInputElement;

    const selectors = `${SbConfirmField.INPUT_SELECTORS} input`;

    if (target.matches(selectors)) {
      this._dirty = true;
      this._value = target.value;
    }
  };

  private _handleFormSubmit = (e: Event) => {
    const valid = this._validate();
    if (!valid) e.preventDefault();
  };

  private _handleFormReset = () => {
    const selectors = SbConfirmField.INPUT_SELECTORS;
    const input = this.querySelector(selectors) as
      | RtgInput
      | RtgPasswordInput
      | null;

    if (input) input.value = this.defaultValue ?? "";

    this._invalid = false;
    this._error = "";
    this._value = "";
    this._dirty = false;
  };

  /* ------------------------------------------------------------ */

  private _renderMark() {
    let content: string;

    if (this.mark) {
      content = "mark";
    } else if (this.required && this.caret) {
      content = "caret";
    } else {
      return nothing;
    }

    return html`
      <span
        id="${this.markId}"
        part="${SbConfirmField.PARTS.mark}"
        data-content="${content}"
      >
        ${this.mark ?? html`&ast;`}
      </span>
    `;
  }

  /* ---------------------------------------- */

  render() {
    return html`
      <rtg-field
        id="${this.rootId}"
        part="${SbConfirmField.PARTS.root}"
        ?disabled=${this.disabled}
        ?invalid=${this._invalid}
      >
        <rtg-field-label
          id="${this.labelId}"
          part="${SbConfirmField.PARTS.label}"
          for="${this.inputId}"
        >
          ${this.label} ${this._renderMark()}
        </rtg-field-label>

        ${this.variant === "toggle"
          ? html`
              <rtg-password-input
                id="${this.inputId}"
                part="${SbConfirmField.PARTS.input}"
                name="${this.name}"
                placeholder="${this.placeholder ?? nothing}"
                autocomplete="${this.autocomplete ?? nothing}"
                ?disabled=${this.disabled}
                ?invalid=${this._invalid}
                ?required=${this.required}
              ></rtg-password-input>
            `
          : html`
              <rtg-input
                id="${this.inputId}"
                part="${SbConfirmField.PARTS.input}"
                name="${this.name}"
                placeholder="${this.placeholder ?? nothing}"
                type="password"
                autocomplete="${this.autocomplete ?? nothing}"
                ?disabled=${this.disabled}
                ?invalid=${this._invalid}
                ?required=${this.required}
              ></rtg-input>
            `}
        ${this._invalid
          ? html`
              <rtg-field-error
                id="${this.errorId}"
                part="${SbConfirmField.PARTS.error}"
                .errors=${[{ message: this._error }]}
              ></rtg-field-error>
            `
          : this.description
            ? html`
                <rtg-field-description
                  id="${this.descriptionId}"
                  part="${SbConfirmField.PARTS.description}"
                >
                  ${this.description}
                </rtg-field-description>
              `
            : nothing}
      </rtg-field>
    `;
  }
}
